import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@common-fe/common-fe';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { toNumber } from 'lodash';

import { api } from '@/api';
import { RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL, IBeneficiaryDto } from '@/dtos';
import { useAuthStore } from '@/modules/user/stores';

import { AppBeneficiaryField, AppBeneficiaryForm } from './common';
import { useHighlightedBeneficiaryStore } from './stores';
import { BeneficiaryRelationship } from './useBeneficiaryRelationshipsTypes.query';
import { BeneficiaryType, GET_BENEFICIARIES_QUERY_KEY } from './useGetBeneficiaries.query';

export const getIsTaxIdMode = (relationship?: BeneficiaryRelationship) =>
  relationship === BeneficiaryRelationship.TRUST || relationship === BeneficiaryRelationship.ESTATE;

interface BeneficiariesPayload {
  id?: number;
  name?: string;
  beneficiary_type?: BeneficiaryType;
  birth_on?: string;
  ssn?: string;
  tax_id?: string;
  relationship_type?: BeneficiaryRelationship;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  allocation_percentage?: number;
}

export const formatFormDataToPayload = (
  formData: AppBeneficiaryForm[],
  beneficiaryType?: BeneficiaryType
): BeneficiariesPayload[] =>
  formData?.map((item) => {
    const relationshipType = item[AppBeneficiaryField.relationshipType];
    const isTaxIdMode = getIsTaxIdMode(relationshipType);

    return {
      ...(item[AppBeneficiaryField.id]
        ? {
            id: toNumber(item[AppBeneficiaryField.id]),
          }
        : {}),
      name: item[AppBeneficiaryField.name],
      beneficiary_type: beneficiaryType,
      ...(isTaxIdMode
        ? {
            tax_id: item[AppBeneficiaryField.taxId],
          }
        : {
            birth_on: dayjs(item[AppBeneficiaryField.birthOn]).format(RAW_DATE_FORMAT),
            ssn: item[AppBeneficiaryField.ssn],
          }),
      relationship_type: relationshipType,
      line1: item[AppBeneficiaryField.line1],
      line2: item[AppBeneficiaryField.line2],
      city: item[AppBeneficiaryField.city],
      state: item[AppBeneficiaryField.state],
      zipcode: item[AppBeneficiaryField.zipcode],
      allocation_percentage: toNumber(item[AppBeneficiaryField.allocationPercentage]),
    };
  });

interface Params {
  isEditMode: boolean;
  onSuccess: () => void;
}

const useCreateUpdateBeneficiariesQuery = ({ isEditMode, onSuccess }: Params) => {
  const { user } = useAuthStore();
  const setHighlightedIds = useHighlightedBeneficiaryStore((state) => state.setHighlightedIds);
  const queryClient = useQueryClient();
  const { isLoading, mutateAsync, isError, isSuccess } = useMutation(
    (payload: BeneficiariesPayload[]) =>
      isEditMode
        ? api.put(API_URL.EMPLOYEES_BENEFICIARIES_BY_EMPLOYEE_ID(user?.employee?.id || ''), payload)
        : api.post(API_URL.EMPLOYEES_BENEFICIARIES_BY_EMPLOYEE_ID(user?.employee?.id || ''), payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_BENEFICIARIES_QUERY_KEY] });
        onSuccess();
      },
    }
  );
  const handleSave = useCallback(
    async (payload: BeneficiariesPayload[]) => {
      const response = (await mutateAsync(payload)) as unknown as AxiosResponse<IBeneficiaryDto[]>;
      const ids = response?.data?.map((item) => `${item.id}`);
      setHighlightedIds(ids);
    },
    [mutateAsync, setHighlightedIds]
  );
  return {
    isLoading,

    isError,
    isSuccess,
    handleSave,
  };
};

export default useCreateUpdateBeneficiariesQuery;
